import { Suspense, useMemo } from "react";
import type { ComponentType } from "react";
import { useSelector } from "react-redux";
import { useBranding } from "../../branding/BrandingProvider";
import { storageIsImpersonateState } from "../../reduxFolder/selectors/globalSelectors";
import { lazyWithRetry } from "../../utils/lazyWithRetry";
import type { ChatConfig } from "../../config/schema";

type ProviderModule<P extends ChatConfig["provider"]> = {
  default: ComponentType<{ config: Extract<ChatConfig, { provider: P }> }>;
};

// Provider loaders keyed by ChatConfig['provider'] discriminator. Each entry
// dynamically imports its provider chunk so PMPAKET (provider: 'none') never
// downloads Intercom, and vice-versa.
//
// `satisfies` enforces that any registered loader resolves to a module whose
// default export accepts the matching ChatConfig variant. Adding zendesk/crisp
// later: drop a directory under providers/, add one entry here.
const PROVIDER_LOADERS = {
  intercom: () =>
    import("./providers/intercom") as Promise<ProviderModule<"intercom">>,
  carrot_quest: () =>
    import("./providers/carrot_quest") as Promise<
      ProviderModule<"carrot_quest">
    >,
  none: () =>
    import("./providers/null") as Promise<ProviderModule<"none">>,
} satisfies Partial<{
  [P in ChatConfig["provider"]]: () => Promise<ProviderModule<P>>;
}>;

// Loader type erased to the union ChatConfig at the boundary. Each provider's
// per-variant type is enforced by `satisfies` on PROVIDER_LOADERS above; the
// runtime mapping (chat.provider → matching loader) makes the union-shaped
// cast safe in practice.
type AnyProviderLoader = () => Promise<{
  default: ComponentType<{ config: ChatConfig }>;
}>;

export function ChatWidget() {
  const { chat, partnerId } = useBranding();
  const isImpersonate = useSelector(storageIsImpersonateState) as boolean;

  // Resolve loader and wrap in lazyWithRetry inside a single useMemo so the
  // hook is called unconditionally on every render (Rules of Hooks). The
  // conditional `return null` checks below are plain JS — safe.
  const Provider = useMemo<ComponentType<{ config: ChatConfig }> | null>(() => {
    const loader =
      PROVIDER_LOADERS[chat.provider as keyof typeof PROVIDER_LOADERS];
    if (!loader) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `[chat] no provider for "${chat.provider}" (partner=${partnerId})`,
        );
      }
      return null;
    }
    return lazyWithRetry(loader as AnyProviderLoader);
  }, [chat.provider, partnerId]);

  // Single source of truth for "should chat render at all". Today: hide during
  // impersonation. Centralized here so consumers (Layout) don't repeat the gate.
  if (isImpersonate) return null;
  if (!Provider) return null;

  return (
    <Suspense fallback={null}>
      <Provider config={chat} />
    </Suspense>
  );
}
